import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/feature/Navbar';
import Footer from '../../../../components/feature/Footer';

export default function ModernCihazYonetimiPage() {
  const [copiedBlock, setCopiedBlock] = useState<string | null>(null);

  const handleCopy = (text: string, blockId: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedBlock(blockId);
      setTimeout(() => setCopiedBlock(null), 2000);
    });
  };

  const entraIdJoinCode = `# PowerShell ile Entra ID Join durumu kontrolü
dsregcmd /status

# Çıktıda aranacak değerler:
# AzureAdJoined : YES
# DomainJoined : NO`;

  const byodCode = `# Kullanıcı tarafından kayıt adımları:
1. Ayarlar > Hesaplar > İş veya okul hesabına erişim
2. Bağlan'a tıkla
3. İş e-posta adresi gir
4. Kimlik doğrulama tamamla`;

  const hybridJoinCode = `# Hybrid join durumu kontrolü
dsregcmd /status

# Çıktıda aranacak değerler:
# AzureAdJoined : YES
# DomainJoined : YES`;

  const securityProfileCode = `{
  "displayName": "Corporate Security Baseline",
  "description": "Kurumsal güvenlik standartları",
  "platforms": "windows10",
  "technologies": "mdm",
  "settings": {
    "defender": {
      "realTimeProtection": "enabled",
      "cloudProtection": "enabled",
      "sampleSubmission": "enabled"
    },
    "firewall": {
      "domainProfile": "enabled",
      "privateProfile": "enabled",
      "publicProfile": "enabled"
    },
    "bitlocker": {
      "requireDeviceEncryption": true,
      "recoveryKeyBackup": "azureAD"
    },
    "windowsUpdate": {
      "updateRing": "General Availability Channel",
      "deferQualityUpdates": 7,
      "deferFeatureUpdates": 14
    }
  }
}`;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/intune" 
            className="inline-flex items-center text-sm text-gray-600 hover:text-orange-600 mb-6 transition-colors"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Intune'a Geri Dön
          </Link>
          
          <div className="mb-8">
            <span className="inline-block px-4 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-4">
              Microsoft Intune
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Modern Cihaz Yönetimi ile Endpoint Güvenliği
            </h1>
            <div className="flex items-center text-gray-600 text-sm">
              <i className="ri-calendar-line mr-2"></i>
              <span>14 Nisan 2025</span>
              <span className="mx-3">•</span>
              <i className="ri-time-line mr-2"></i>
              <span>8 dakika okuma</span>
            </div>
          </div>

          <img 
            src="https://public.readdy.ai/ai/img_res/648fba0cb162d60bfee22bfe0cf24056.jpg"
            alt="Modern Cihaz Yönetimi"
            className="w-full h-96 object-cover rounded-2xl mb-12"
          />
        </div>
      </section>

      {/* Article Content */}
      <article className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Modern çalışma ortamlarında, çalışanlar farklı cihazlardan ve lokasyonlardan şirket kaynaklarına erişim sağlıyor. Bu değişen iş yapış şekli, geleneksel cihaz yönetimi yaklaşımlarının yetersiz kalmasına neden oldu. Microsoft Intune, bulut tabanlı modern cihaz yönetimi ile bu zorluklara çözüm sunuyor.
              </p>
            </div>

            {/* MDM vs MAM */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">MDM ve MAM Arasındaki Fark</h2>
              
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  <i className="ri-smartphone-line text-orange-600 mr-3"></i>
                  Mobile Device Management (MDM)
                </h3>
                <p className="text-gray-700 mb-4">
                  MDM, cihazın tamamını yönetme yetkisi verir. Şirket tarafından sağlanan cihazlarda kullanılır ve IT departmanına tam kontrol sağlar.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                    <span>Cihaz düzeyinde yapılandırma ve güvenlik politikaları</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                    <span>Uzaktan cihaz silme ve kilitleme</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                    <span>Uygulama dağıtımı ve güncellemeleri</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                    <span>Donanım ve yazılım envanteri</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  <i className="ri-apps-line text-orange-600 mr-3"></i>
                  Mobile Application Management (MAM)
                </h3>
                <p className="text-gray-700 mb-4">
                  MAM, cihazın tamamını değil, sadece iş uygulamalarını yönetir. BYOD (Bring Your Own Device) senaryolarında idealdir.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                    <span>Uygulama düzeyinde veri koruma</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                    <span>Kopyala-yapıştır ve paylaşım kısıtlamaları</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                    <span>Şifreli uygulama verileri</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                    <span>Kullanıcı gizliliğini koruma</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Enrollment Scenarios */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Cihaz Kayıt Senaryoları</h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-orange-600 pl-6 py-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    1. Entra ID Join (Şirket Cihazları)
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Cihaz doğrudan Entra ID'ye katılır ve Intune'a otomatik kaydolur. Şirket tarafından sağlanan Windows cihazlar için en uygun yöntem.
                  </p>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative">
                    <button
                      onClick={() => handleCopy(entraIdJoinCode, 'entra')}
                      className="absolute top-3 right-3 text-orange-400 hover:text-orange-300 text-sm cursor-pointer transition-colors"
                    >
                      {copiedBlock === 'entra' ? (
                        <><i className="ri-check-line mr-1"></i>Kopyalandı</>
                      ) : (
                        <><i className="ri-file-copy-line mr-1"></i>Kopyala</>
                      )}
                    </button>
                    <pre className="text-sm text-gray-100">
{entraIdJoinCode}
                    </pre>
                  </div>
                </div>

                <div className="border-l-4 border-amber-600 pl-6 py-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    2. Entra ID Registered (BYOD)
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Kullanıcının kişisel cihazı, iş hesabı ile kaydedilir. Kullanıcı cihazın sahibi olmaya devam eder, sadece iş uygulamaları yönetilir.
                  </p>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative">
                    <button
                      onClick={() => handleCopy(byodCode, 'byod')}
                      className="absolute top-3 right-3 text-orange-400 hover:text-orange-300 text-sm cursor-pointer transition-colors"
                    >
                      {copiedBlock === 'byod' ? (
                        <><i className="ri-check-line mr-1"></i>Kopyalandı</>
                      ) : (
                        <><i className="ri-file-copy-line mr-1"></i>Kopyala</>
                      )}
                    </button>
                    <pre className="text-sm text-gray-100">
{byodCode}
                    </pre>
                  </div>
                </div>

                <div className="border-l-4 border-red-600 pl-6 py-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    3. Hybrid Entra ID Join (Geçiş Senaryosu)
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Cihaz hem on-premises Active Directory hem de Entra ID'ye katılır. Geleneksel AD ortamından buluta geçiş yapan kuruluşlar için.
                  </p>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative">
                    <button
                      onClick={() => handleCopy(hybridJoinCode, 'hybrid')}
                      className="absolute top-3 right-3 text-orange-400 hover:text-orange-300 text-sm cursor-pointer transition-colors"
                    >
                      {copiedBlock === 'hybrid' ? (
                        <><i className="ri-check-line mr-1"></i>Kopyalandı</>
                      ) : (
                        <><i className="ri-file-copy-line mr-1"></i>Kopyala</>
                      )}
                    </button>
                    <pre className="text-sm text-gray-100">
{hybridJoinCode}
                    </pre>
                  </div>
                </div>
              </div>
            </section>

            {/* Compliance Policies */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Uyumluluk Politikaları</h2>
              
              <p className="text-gray-700 mb-6">
                Intune uyumluluk politikaları, cihazların güvenlik standartlarını karşılamasını sağlar. Uyumsuz cihazların şirket kaynaklarına erişimi engellenebilir.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-orange-600 transition-colors">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-shield-check-line text-2xl text-orange-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Güvenlik Gereksinimleri</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Minimum işletim sistemi sürümü</li>
                    <li>• BitLocker şifreleme zorunluluğu</li>
                    <li>• Güvenlik duvarı aktif olmalı</li>
                    <li>• Antivirüs güncel olmalı</li>
                  </ul>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-orange-600 transition-colors">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-lock-password-line text-2xl text-amber-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Parola Politikaları</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Minimum parola uzunluğu</li>
                    <li>• Karmaşıklık gereksinimleri</li>
                    <li>• Biyometrik kimlik doğrulama</li>
                    <li>• Maksimum boşta kalma süresi</li>
                  </ul>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-orange-600 transition-colors">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-smartphone-line text-2xl text-red-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Cihaz Sağlığı</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Jailbreak/Root kontrolü</li>
                    <li>• Cihaz sağlık attestation</li>
                    <li>• Güvenli önyükleme aktif</li>
                    <li>• Code integrity kontrolü</li>
                  </ul>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-orange-600 transition-colors">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-alert-line text-2xl text-red-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Tehdit Koruması</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Defender ATP entegrasyonu</li>
                    <li>• Tehdit seviyesi eşikleri</li>
                    <li>• Otomatik karantina</li>
                    <li>• Risk tabanlı erişim</li>
                  </ul>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-xl">
                <div className="flex items-start">
                  <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">En İyi Uygulama</h4>
                    <p className="text-gray-700 text-sm">
                      Uyumluluk politikalarını Conditional Access ile birleştirerek, uyumsuz cihazların şirket kaynaklarına erişimini otomatik olarak engelleyin. Bu yaklaşım, güvenlik açıklarını minimize eder.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Configuration Profiles */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Yapılandırma Profilleri</h2>
              
              <p className="text-gray-700 mb-6">
                Yapılandırma profilleri, cihaz ayarlarını merkezi olarak yönetmenizi sağlar. Kullanıcı müdahalesi olmadan güvenlik ve yapılandırma ayarları uygulanır.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-shield-check-line text-2xl text-orange-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Güvenlik Ayarları</h3>
                  <p className="text-gray-600">
                    BitLocker şifreleme, güvenlik duvarı kuralları, parola politikaları ve Windows Defender yapılandırması.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-settings-3-line text-2xl text-orange-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Cihaz Kısıtlamaları</h3>
                  <p className="text-gray-600">
                    USB erişimi, kamera kullanımı, uygulama yükleme izinleri ve ağ bağlantı ayarları.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-mail-line text-2xl text-orange-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">E-posta Profilleri</h3>
                  <p className="text-gray-600">
                    Exchange, Office 365 ve diğer e-posta hesaplarının otomatik yapılandırması.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-wifi-line text-2xl text-orange-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Wi-Fi ve VPN</h3>
                  <p className="text-gray-600">
                    Kurumsal Wi-Fi ağları ve VPN bağlantılarının otomatik kurulumu ve yönetimi.
                  </p>
                </div>
              </div>
            </section>

            {/* Conditional Access */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Conditional Access Entegrasyonu</h2>
              
              <p className="text-gray-700 mb-6">
                Intune uyumluluk durumu, Entra ID Conditional Access politikalarında kullanılarak akıllı erişim kontrolü sağlanır.
              </p>

              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Örnek Senaryo: Uyumlu Cihaz Zorunluluğu</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                      1
                    </div>
                    <div>
                      <strong className="text-gray-900">Kullanıcı oturum açar</strong>
                      <p className="text-gray-700 text-sm">Entra ID kimlik doğrulaması başlar</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                      2
                    </div>
                    <div>
                      <strong className="text-gray-900">Cihaz uyumluluğu kontrol edilir</strong>
                      <p className="text-gray-700 text-sm">Intune'dan uyumluluk durumu sorgulanır</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                      3
                    </div>
                    <div>
                      <strong className="text-gray-900">Erişim kararı verilir</strong>
                      <p className="text-gray-700 text-sm">Uyumlu ise erişim izni, değilse engelleme</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                      4
                    </div>
                    <div>
                      <strong className="text-gray-900">Kullanıcı bilgilendirilir</strong>
                      <p className="text-gray-700 text-sm">Uyumsuzluk nedenleri ve çözüm adımları gösterilir</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Best Practices */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">En İyi Uygulamalar</h2>
              
              <div className="space-y-4">
                <div className="flex items-start bg-white border-2 border-gray-200 rounded-xl p-6">
                  <i className="ri-checkbox-circle-line text-2xl text-orange-600 mr-4 mt-1"></i>
                  <div>
                    <strong className="text-gray-900 block mb-2">Aşamalı Dağıtım Yapın</strong>
                    <p className="text-gray-700 text-sm">
                      Yeni politikaları önce pilot gruplarda test edin, ardından tüm organizasyona yaygınlaştırın.
                    </p>
                  </div>
                </div>

                <div className="flex items-start bg-white border-2 border-gray-200 rounded-xl p-6">
                  <i className="ri-checkbox-circle-line text-2xl text-orange-600 mr-4 mt-1"></i>
                  <div>
                    <strong className="text-gray-900 block mb-2">Kullanıcı Deneyimini Önceliklendirin</strong>
                    <p className="text-gray-700 text-sm">
                      Güvenlik politikaları kullanıcı verimliliğini engellememelidir. Dengeli bir yaklaşım benimseyin.
                    </p>
                  </div>
                </div>

                <div className="flex items-start bg-white border-2 border-gray-200 rounded-xl p-6">
                  <i className="ri-checkbox-circle-line text-2xl text-orange-600 mr-4 mt-1"></i>
                  <div>
                    <strong className="text-gray-900 block mb-2">Raporlama ve İzleme</strong>
                    <p className="text-gray-700 text-sm">
                      Intune raporlarını düzenli olarak inceleyin, uyumsuz cihazları proaktif olarak yönetin.
                    </p>
                  </div>
                </div>

                <div className="flex items-start bg-white border-2 border-gray-200 rounded-xl p-6">
                  <i className="ri-checkbox-circle-line text-2xl text-orange-600 mr-4 mt-1"></i>
                  <div>
                    <strong className="text-gray-900 block mb-2">Dokümantasyon</strong>
                    <p className="text-gray-700 text-sm">
                      Tüm politikaları ve yapılandırmaları dokümante edin, değişiklikleri versiyon kontrolü ile takip edin.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Articles */}
            <section className="mt-16 pt-12 border-t-2 border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">İlgili Makaleler</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Link 
                  to="/intune/uygulama-koruma-politikalari"
                  className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-orange-600 transition-all"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                    Uygulama Koruma Politikaları
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    MAM politikaları ile BYOD senaryolarında veri güvenliğini sağlama
                  </p>
                  <span className="text-orange-600 text-sm font-medium">
                    Devamını Oku <i className="ri-arrow-right-line ml-1"></i>
                  </span>
                </Link>

                <Link 
                  to="/intune/windows-autopilot"
                  className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-orange-600 transition-all"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                    Windows Autopilot
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Sıfır dokunuş cihaz dağıtımı ile IT yükünü azaltın
                  </p>
                  <span className="text-orange-600 text-sm font-medium">
                    Devamını Oku <i className="ri-arrow-right-line ml-1"></i>
                  </span>
                </Link>
              </div>
            </section>

          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
